import './ActivityLog.scss';

import { Avatar, Box, Typography } from '@mui/material';
import React from 'react';
import { useFetchList } from 'services/common/react-query-common-service';

import StylishLoader from '../../components/StylishLoader';

interface ActivityLogItem {
  id: string;
  jobId: string;
  action: string;
  createdAt: string;
  createdBy: string | null;
  meta: {
    action: string;
    message: string;
    actionDoneBy?: string;
    [key: string]: any;
  };
}

interface ActivityGroup {
  loggedOn: string;
  logs: ActivityLogItem[];
}

interface ActivityLogProps {
  jobId?: string;
}

const ActivityLog: React.FC<ActivityLogProps> = ({ jobId }) => {
  const { data, isLoading } = useFetchList<ActivityGroup[]>(
    ['job-activity-logs', jobId],
    `jobs/activity-logs/${String(jobId)}`,
    {
      enabled: !!jobId,
      refetchOnMount: 'always',
    },
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="200px">
        <StylishLoader mode="table" text="Fetching activity logs..." />
      </Box>
    );
  }

  const activityGroups = data || [];

  if (activityGroups.length === 0 || !activityGroups[0]?.logs?.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="200px"
        className="no-activity-container">
        <span className="no-activity-text">No activity log found</span>
      </Box>
    );
  }

  const formatDateLabel = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();

      // Extract literal Year, Month, Day from UTC string (e.g. "2026-04-16")
      const [y, m, d] = dateStr.split('T')[0].split('-').map(Number);

      // Get Local Year, Month, Day
      const curY = now.getFullYear();
      const curM = now.getMonth() + 1; // getMonth is 0-indexed
      const curD = now.getDate();

      // Today Check
      if (y === curY && m === curM && d === curD) {
        return 'Today';
      }

      // Yesterday Check
      const yesterday = new Date(now);
      yesterday.setDate(now.getDate() - 1);
      const yestY = yesterday.getFullYear();
      const yestM = yesterday.getMonth() + 1;
      const yestD = yesterday.getDate();

      if (y === yestY && m === yestM && d === yestD) {
        return 'Yesterday';
      }

      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        hour12: true,
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  };

  const parseUserInfo = (actionStr: string) => {
    if (!actionStr) return { initial: 'U', name: '' };

    // Attempt to extract name from "Action by User Name"
    const byIndex = actionStr.lastIndexOf(' by ');
    if (byIndex !== -1) {
      const name = actionStr.substring(byIndex + 4).trim();
      return {
        initial: name ? name[0].toUpperCase() : 'U',
        name,
      };
    }
    return { initial: 'U', name: '' };
  };

  return (
    <Box className="activity-log-container">
      {activityGroups.map((group) => (
        <Box key={group.loggedOn} className="activity-group">
          <Typography className="group-date">
            {formatDateLabel(group.loggedOn)}
          </Typography>
          <Box className="activity-list">
            {group.logs.map((item) => {
              const { name, initial } = parseUserInfo(item.meta.action);
              const isSystem = !item.createdBy;
              const isManual = !!item.createdBy;

              const displayMessage = item.meta.message || '';

              return (
                <Box key={item.id} className="activity-item">
                  <Box className="avatar-section">
                    {isSystem ? (
                      <Avatar className="user-avatar">S</Avatar>
                    ) : isManual ? (
                      <Avatar className="user-avatar">M</Avatar>
                    ) : (
                      <Avatar className="user-avatar">{initial}</Avatar>
                    )}
                  </Box>
                  <Box className="content-section">
                    <Box className="action-text">
                      {isSystem || isManual || name ? (
                        <>
                          <span className="user-info">
                            {isSystem ? 'System' : isManual ? 'Manual' : name}
                          </span>
                          <span className="dot-separator"> · </span>
                        </>
                      ) : null}
                      <span className="action-description">
                        {displayMessage}
                      </span>
                    </Box>
                    {/* {displayMessage && displayMessage !== displayAction && (
                      <Typography className="sub-description">
                        {displayMessage}
                      </Typography>
                    )} */}
                    <Typography className="timestamp">
                      {formatTime(item.createdAt)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default ActivityLog;
